import { useEffect, useMemo } from "react";
import { useEditor } from "../../hooks/useEditor";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useMessage } from "../../hooks/useMessage";
import { useStableCallback } from "../../hooks/useStableCallback";

/**
 * Create an object to check if spreads are dirty
 * Because spreads are mutated, we cannot compare references,
 * we need to listen to the right messages to detect changes.
 */
export function useSpreadDirtyChecker(
	/**
	 * When dirty spreads are detected, this method will be called
	 * As an argument the SpreadDirtyChecker methods are passed, so you can check if your spreads in view are dirty
	 * Remark: This is called synchronious on a message, so react state could not be up to date yet
	 */
	onDirtySpread: (methods: ReturnType<typeof useSpreadDirtyChecker>) => void,
) {
	const editor = useEditor();

	const methods = useMemo(() => {
		/*
		 * Map of 'clean' spreads.
		 * All spreads that are NOT dirty should be in this list, so by default new spreads are dirty
		 */
		const cleanSpreads = new WeakMap<eXpress.core.Spread, true>();

		/** Check if the spread is dirty */
		function isDirty(spread: eXpress.core.Spread) {
			return !cleanSpreads.get(spread);
		}

		/** Clear the dirty flag for this spread */
		function clearDirty(spread: eXpress.core.Spread) {
			cleanSpreads.set(spread, true);
		}

		/** Mark a spread as Dirty */
		function setDirty(spread: eXpress.core.Spread) {
			cleanSpreads.delete(spread);
		}

		return {
			cleanSpreads,
			isDirty,
			clearDirty,
			setDirty,
		};
	}, []);

	// Make all spreads dirty when PreviewMode changes
	const [isPreviewMode] = useIsPreviewMode();
	useEffect(() => {
		const { definition } = editor;
		if (definition) {
			definition.spreads.forEach((spread) => {
				methods.setDirty(spread);
			});
		}
	}, [editor, isPreviewMode, methods]);

	// Make current spread dirty when TransformMode changes
	const [isTransformMode] = useIsTransformMode();
	useEffect(() => {
		const selectedSpread = editor.selection.getCurrentSpread();
		if (selectedSpread) {
			methods.setDirty(selectedSpread);
		}
	}, [editor, methods, isTransformMode]);

	useMessage(
		useStableCallback((message) => {
			let isSpreadDirty = false;

			// TODO: when new functionallity is added to the Mobile Creator, we should add the messages that can make spread dirty (rerender needed)
			// The useMessage handler in this component will call onDirtySpread, the callback can check if the spreads in view are dirty
			if (eXpress.core.affectsSpreads(message)) {
				const affectedSpreads = message.getAffectedSpreads();
				affectedSpreads.forEach((spread) => {
					methods.cleanSpreads.delete(spread); // remove as clean spread
				});
				isSpreadDirty = true;
			}

			// If a partner photo is downloaded or rejected on the server, make the spreads containing the photo dirty (= requires rerender)
			// (Also for spreads not in view)
			if (eXpress.core.isPhotoStateChangedForSpreadPhotos(message, undefined)) {
				const { definition } = editor;
				let isDirty = false;
				definition?.spreads.forEach((spread) => {
					if (eXpress.core.isPhotoStateChangedForSpreadPhotos(message, spread)) {
						methods.cleanSpreads.delete(spread); // remove as clean spread
						isDirty = true;
					}
				});
				if (isDirty) isSpreadDirty = true;
			}

			// Check if a new adorner is needed (Product Option/ Attribute is changed)
			// We now do this by rerendering everything (adorner + spread) even is spread is not changed
			if (eXpress.core.shouldUpdateAdorners(message)) {
				const { definition } = editor;
				definition?.spreads.forEach((spread) => {
					methods.cleanSpreads.delete(spread); // remove as clean spread
				});
				isSpreadDirty = true;
			}

			if (isSpreadDirty) onDirtySpread(methods);
		}),
		{
			captureInteractiveMessages: true, // Like text Change
		},
	);

	return methods;
}
